// A room's glazings on the plan (docs/claude/glazings.md): the real sections of
// their profiles, glass, seals and handle where the plan cuts them
// (roomGlazing.ts `glazingPlanCut`), and the door's swing about its hinges'
// axis. Drawn by render.ts over the room's walls, in place of the plain panes
// and leaf a glazing's door and panes would otherwise be.

import type {
  ProductInstance,
  Vec2,
} from '../shared/types';
import type { GlazingStuff } from './glazing';
import {
  glazingPartOf,
  glazingPlanCut,
  glazingSwing,
  type RoomGlazing,
  roomGlazings,
} from './roomGlazing';
import { strokeSwing } from './doorSwing';

export interface GlazingPlanStyle {
  /** The light (print) look, else the dark screen's. */
  light: boolean;
  selectedDoorId: string | null;
  selectedPaneIds: ReadonlySet<string>;
  /** The room's walls' colour on the plan: a wood frame's fill. */
  woodFill: string;
}

const FILL: Record<Exclude<GlazingStuff, 'wood'>, { dark: string; light: string }> = {
  gasket: { dark: '#55595e', light: '#55595e' },
  glass: { dark: 'rgba(120,190,230,0.55)', light: 'rgba(120,190,230,0.45)' },
  hardware: { dark: '#b2b6bb', light: '#8c9096' },
  magnet: { dark: '#4a4d50', light: '#4a4d50' },
  profile: { dark: '#9aa0a6', light: '#b4b9be' },
  seal: { dark: 'rgba(240,244,243,0.75)', light: 'rgba(160,170,170,0.6)' },
  silicone: { dark: 'rgba(238,243,242,0.6)', light: 'rgba(150,160,160,0.5)' },
};

/** Draw every glazing of a room: `toS` takes the room's local plan to the screen. */
export function drawGlazingsPlan(ctx: CanvasRenderingContext2D, inst: ProductInstance, toS: (p: Vec2) => Vec2, style: GlazingPlanStyle): void {
  const gs = roomGlazings(inst);
  if (!gs.length) return;
  ctx.save();
  ctx.lineJoin = 'round';
  for (const g of gs) drawOne(ctx, g, toS, style);
  ctx.restore();
}

/** A cut solid ready to draw: its paths in the room's local plan, built once
 *  per glazing (kept with it, as its cut is), and its size for the detail. */
interface CutPath { stuff: GlazingStuff; part: { kind: 'door' | 'pane'; id: string }; exact: boolean; full: Path2D; outer: Path2D; size: number }
const PATHS = new WeakMap<RoomGlazing, CutPath[]>();

function cutPaths(g: RoomGlazing): CutPath[] {
  let out = PATHS.get(g);
  if (out) return out;
  out = glazingPlanCutWithParts(g).map((c) => {
    const ring = (p: Path2D, r: Vec2[]): void => { r.forEach((q, i) => (i === 0 ? p.moveTo(q.x, q.y) : p.lineTo(q.x, q.y))); p.closePath(); };
    const full = new Path2D();
    for (const r of c.rings) ring(full, r);
    let outer = full;
    if (c.exact && c.rings.length > 1) { outer = new Path2D(); ring(outer, c.rings[0]); }
    let x0 = Infinity, y0 = Infinity, x1 = -Infinity, y1 = -Infinity;
    for (const r of c.rings) for (const q of r) { x0 = Math.min(x0, q.x); y0 = Math.min(y0, q.y); x1 = Math.max(x1, q.x); y1 = Math.max(y1, q.y); }
    return { stuff: c.stuff, part: c.part, exact: c.exact, full, outer, size: Math.max(x1 - x0, y1 - y0) };
  });
  PATHS.set(g, out);
  return out;
}

/** Below this many pixels a solid is drawn as its outline alone (no holes, no
 *  edge line); below MIN_PX a seal, gasket or magnet is not drawn at all. */
const DETAIL_PX = 6;
const MIN_PX = 1.5;
const MINOR: ReadonlySet<GlazingStuff> = new Set<GlazingStuff>(['gasket', 'magnet', 'seal', 'silicone']);

function drawOne(ctx: CanvasRenderingContext2D, g: RoomGlazing, toS: (p: Vec2) => Vec2, style: GlazingPlanStyle): void {
  const edge = style.light ? '#333333' : '#e3e6ea';
  // The room's local plan to the screen is affine (a turn, a uniform scale and
  // the y flip): the cached paths are drawn under it, the widths in pixels.
  const o = toS({ x: 0, y: 0 }), ex = toS({ x: 1, y: 0 }), ey = toS({ x: 0, y: 1 });
  const scale = Math.hypot(ex.x - o.x, ex.y - o.y);
  if (!(scale > 0)) return;
  ctx.save();
  ctx.transform(ex.x - o.x, ex.y - o.y, ey.x - o.x, ey.y - o.y, o.x, o.y);
  for (const c of cutPaths(g)) {
    const px = c.size * scale;
    if (px < MIN_PX && MINOR.has(c.stuff)) continue;
    const detailed = px >= DETAIL_PX;
    const path = detailed ? c.full : c.outer;
    const part = c.part;
    const chosen = part.kind === 'door' ? style.selectedDoorId === part.id : style.selectedPaneIds.has(part.id);
    if (chosen) {
      ctx.strokeStyle = part.kind === 'door' ? '#ffd166' : '#22e650';
      ctx.lineWidth = 4 / scale;
      ctx.stroke(path);
    }
    ctx.fillStyle = c.stuff === 'wood' ? style.woodFill : FILL[c.stuff][style.light ? 'light' : 'dark'];
    ctx.fill(path, c.exact && detailed ? 'evenodd' : 'nonzero');
    if (detailed && c.exact && (c.stuff === 'profile' || c.stuff === 'wood' || c.stuff === 'hardware')) {
      ctx.strokeStyle = edge;
      ctx.lineWidth = 0.6 / scale;
      ctx.stroke(path);
    }
  }
  ctx.restore();
  const sw = glazingSwing(g);
  if (!sw) return;
  const doorChosen = style.selectedDoorId === g.door.id;
  ctx.strokeStyle = doorChosen ? '#ffd166' : '#e7b15a';
  ctx.lineWidth = doorChosen ? 2 : 1.2;
  strokeSwing(ctx, sw, toS, { leaf: false, segs: 24 });
  const h = toS(sw.hinge);
  ctx.fillStyle = ctx.strokeStyle;
  ctx.beginPath();
  ctx.arc(h.x, h.y, 2.5, 0, Math.PI * 2);
  ctx.fill();
}

/** The plan's cut of a glazing, each solid with what a click on it takes. */
export function glazingPlanCutWithParts(g: RoomGlazing): Array<ReturnType<typeof glazingPlanCut>[number] & { part: { kind: 'door' | 'pane'; id: string } }> {
  return glazingPlanCut(g).map((c) => ({ ...c, part: glazingPartOf(g, c.solid) }));
}
